import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslate, TFnType } from '@tolgee/react';
import { initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { StatisticsOrderLossRecoveryOfDay } from 'services/api/dashboard';
import { percentDisplay, sortByDate } from 'views/Dashboard/ImplementData/utils';
import { useSize } from 'ahooks';

const Container = styled.div`
  width: 100%;
  height: 330px;
`;

const barOption = (value: StatisticsOrderLossRecoveryOfDay[], t: TFnType): EChartsOption => {
  const data = value;
  const dateRange = data?.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      show: true,
      realtime: true,
      start: 0,
      end: 100,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
    },
  ];
  return {
    grid: {
      left: '6%',
      right: '6%',
    },
    dataZoom: dateRange.length > 10 ? dataZoom : undefined,
    legend: {
      data: [
        t('ViewDashboard.损耗订单数'),
        t('ViewDashboard.撤销弃单申请数'),
        t('ViewDashboard.弃单后再次下单数'),
        t('ViewDashboard.订单挽回率'),
      ],
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
        name: '',
      },
      {
        type: 'value',
        name: '%',
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.orderLossCount || 0),
        type: 'bar',
        name: t('ViewDashboard.损耗订单数'),
        tooltip: {
          valueFormatter: (value) => `${value}${t('ViewDashboard.笔(单位)')}`,
        },
        itemStyle: {
          color: '#5B8FF9',
        },
      },
      {
        data: data.map((x) => x.orderLossCancelledCount || 0),
        type: 'bar',
        name: t('ViewDashboard.撤销弃单申请数'),
        tooltip: {
          valueFormatter: (value) => `${value}${t('ViewDashboard.笔(单位)')}`,
        },
        itemStyle: {
          color: '#5AD8A6',
        },
      },
      {
        data: data.map((x) => x.orderLossReorderCount || 0),
        type: 'bar',
        name: t('ViewDashboard.弃单后再次下单数'),
        tooltip: {
          valueFormatter: (value) => `${value}${t('ViewDashboard.笔(单位)')}`,
        },
        itemStyle: {
          color: '#5d7092',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => (x.orderBackRate || 0) * 100),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value) => `${percentDisplay(Number(value) / 100)}`,
        },
        name: t('ViewDashboard.订单挽回率'),
        lineStyle: {
          color: '#f6903d',
          opacity: 0.85,
        },
        itemStyle: {
          color: '#f6903d',
        },
      },
    ],
  };
};

interface Props {
  value?: StatisticsOrderLossRecoveryOfDay[];
}
const RecoverChart = (props: Props) => {
  const { value = [] } = props;
  const { t } = useTranslate();
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  useEffect(() => {
    if (!value || !chartDomRef.current) {
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const val = sortByDate(value) || [];
    const options = barOption(val || [], t);
    chartRef.current.setOption(applyChartTheme(options));
  }, [value, t]);
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);
  return <Container className="bar" ref={chartDomRef} />;
};

export default RecoverChart;
